import React, { useState, useCallback, useMemo, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import classNames from 'classnames';
import { NativeProps, CSpacing, CSeverity } from '@/src/components/interfaces';
import { CBottomPanel } from '@/src/components/c-bottom-panel/CBottomPanel';
import { usePresenterStore } from '@/src/infrastructure/presenter/store';
import { CButton, CButtonPattern, CButtonShape } from '@/src/components/c-button/CButton';
import { CFlex, JustifyContent } from '@/src/components/c-flex/CFlex';
import { CIcons } from '@/src/components/c-icon/CIcon';
import { CText, CTextSize, CTextColor } from '@/src/components/c-text/CText';
import { CFieldLabel, CFieldSecurity } from '@/src/components/c-field-label/CFieldLabel';
import { CFieldValidation, FieldValidation } from '@/src/components/c-field-validations/CFieldValidation';
import { useCTranslations, useCPortal, CPortalLayer } from '@/src/infrastructure/provider';
import { CWheel, CWheelItem } from '@/src/components/c-wheel/CWheel';
import { CTimePickerMinuteStep } from '@/src/components/c-time-picker/CTimePicker';

export interface CDateTimePickerTranslations {
  cancel?: string;
  confirm?: string;
  next?: string;
  back?: string;
  today?: string;
  now?: string;
  title?: string;
  months?: string[];
}

export interface CDateTimeValue {
  date: Date;
  hour: number;
  minute: number;
}

export interface CDateTimePickerProps extends NativeProps {
  value?: CDateTimeValue;
  onChange: (value: CDateTimeValue) => void;
  min?: Date;
  max?: Date;
  minuteStep?: CTimePickerMinuteStep;
  label?: string;
  placeholder?: string;
  disabled?: boolean;
  required?: boolean;
  fullWidth?: boolean;
  security?: CFieldSecurity;
  validations?: FieldValidation[];
  hideMessageContainer?: boolean;
  translations?: CDateTimePickerTranslations;
}

enum Step {
  Date = 'date',
  Time = 'time',
}

function getDaysInMonth(year: number, month: number): number {
  return new Date(year, month + 1, 0).getDate();
}

function clampValue(value: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, value));
}

const HOUR_ITEMS: CWheelItem[] = Array.from({ length: 24 }, (_, i) => ({ value: i, label: String(i).padStart(2, '0') }));

function getMinuteItems(step: number): CWheelItem[] {
  const count = Math.floor(60 / step);
  return Array.from({ length: count }, (_, i) => {
    const value = i * step;
    return { value, label: String(value).padStart(2, '0') };
  });
}

const isMobile = () => window.innerWidth <= 480;

export function CDateTimePicker(props: CDateTimePickerProps): React.JSX.Element {
  const translations = useCTranslations();
  const now = new Date();
  const minuteStep = props.minuteStep ?? CTimePickerMinuteStep.One;
  const initial = props.value ?? { date: now, hour: now.getHours(), minute: now.getMinutes() };

  const [selectedYear, setSelectedYear] = useState(initial.date.getFullYear());
  const [selectedMonth, setSelectedMonth] = useState(initial.date.getMonth());
  const [selectedDay, setSelectedDay] = useState(initial.date.getDate());
  const [selectedHour, setSelectedHour] = useState(initial.hour);
  const [selectedMinute, setSelectedMinute] = useState(initial.minute);
  const [step, setStep] = useState<Step>(Step.Date);
  const [open, setOpen] = useState(false);
  const presenter = usePresenterStore();
  const portalContainer = useCPortal(CPortalLayer.Popover);
  const triggerRef = useRef<HTMLDivElement>(null);
  const [dropdownStyle, setDropdownStyle] = useState<React.CSSProperties>({});

  const minYear = props.min?.getFullYear() ?? now.getFullYear() - 100;
  const maxYear = props.max?.getFullYear() ?? now.getFullYear() + 10;
  const monthNames = props.translations?.months ?? translations.datePicker.months;
  const minuteItems = useMemo(() => getMinuteItems(minuteStep), [minuteStep]);

  useEffect(() => {
    if (props.value) {
      setSelectedYear(props.value.date.getFullYear());
      setSelectedMonth(props.value.date.getMonth());
      setSelectedDay(props.value.date.getDate());
      setSelectedHour(props.value.hour);
      setSelectedMinute(props.value.minute);
    }
  }, [props.value]);

  const yearItems = useMemo(() =>
    Array.from({ length: maxYear - minYear + 1 }, (_, i) => ({ value: minYear + i, label: String(minYear + i) })),
  [minYear, maxYear]);

  const monthItems = useMemo(() =>
    monthNames.map((name, i) => ({ value: i, label: name })),
  [monthNames]);

  const daysInCurrentMonth = getDaysInMonth(selectedYear, selectedMonth);
  const dayItems = useMemo(() =>
    Array.from({ length: daysInCurrentMonth }, (_, i) => ({ value: i + 1, label: String(i + 1).padStart(2, '0') })),
  [daysInCurrentMonth]);

  const clampedDay = Math.min(selectedDay, daysInCurrentMonth);

  const handleConfirm = useCallback(() => {
    const safeDay = clampValue(selectedDay, 1, getDaysInMonth(selectedYear, selectedMonth));
    props.onChange({ date: new Date(selectedYear, selectedMonth, safeDay), hour: selectedHour, minute: selectedMinute });
    setOpen(false);
    setStep(Step.Date);
    presenter.reject();
  }, [selectedYear, selectedMonth, selectedDay, selectedHour, selectedMinute, props.onChange, presenter]);

  const handleCancel = useCallback(() => {
    if (props.value) {
      setSelectedYear(props.value.date.getFullYear());
      setSelectedMonth(props.value.date.getMonth());
      setSelectedDay(props.value.date.getDate());
      setSelectedHour(props.value.hour);
      setSelectedMinute(props.value.minute);
    }
    setOpen(false);
    setStep(Step.Date);
    presenter.reject();
  }, [props.value, presenter]);

  const handleNext = useCallback(() => setStep(Step.Time), []);
  const handleBack = useCallback(() => setStep(Step.Date), []);

  const handleToday = useCallback(() => {
    const t = new Date();
    setSelectedYear(t.getFullYear());
    setSelectedMonth(t.getMonth());
    setSelectedDay(t.getDate());
  }, []);

  const handleNow = useCallback(() => {
    const t = new Date();
    setSelectedHour(t.getHours());
    setSelectedMinute(Math.floor(t.getMinutes() / minuteStep) * minuteStep);
  }, [minuteStep]);

  const handleOpen = useCallback(() => {
    if (props.disabled) return;
    if (isMobile()) {
      void presenter.show(undefined as any);
    } else {
      if (triggerRef.current) {
        const rect = triggerRef.current.getBoundingClientRect();
        setDropdownStyle({ top: rect.bottom + 4, left: rect.left, width: Math.max(rect.width, 300) });
      }
      setOpen(true);
    }
  }, [props.disabled, presenter]);

  const displayValue = props.value
    ? `${String(props.value.date.getDate()).padStart(2, '0')} ${monthNames[props.value.date.getMonth()]} ${props.value.date.getFullYear()} ${String(props.value.hour).padStart(2, '0')}:${String(props.value.minute).padStart(2, '0')}`
    : '';

  const status = useMemo(() => {
    const hasErrors = props.validations?.some(v => v.severity === CSeverity.Error) ?? false;
    const hasWarnings = props.validations?.some(v => v.severity === CSeverity.Warning) ?? false;
    if (hasErrors) return 'error';
    if (hasWarnings) return 'warning';
    return 'neutral';
  }, [props.validations]);

  const dateWheels = (
    <div className="c-date-time-picker__wheels">
      <CWheel items={dayItems} selected={clampedDay} onChange={setSelectedDay} />
      <CWheel items={monthItems} selected={selectedMonth} onChange={setSelectedMonth} />
      <CWheel items={yearItems} selected={selectedYear} onChange={setSelectedYear} />
    </div>
  );

  const timeWheels = (
    <div className="c-date-time-picker__wheels">
      <CWheel items={HOUR_ITEMS} selected={selectedHour} onChange={setSelectedHour} />
      <div className="c-date-time-picker__separator">:</div>
      <CWheel items={minuteItems} selected={selectedMinute} onChange={setSelectedMinute} />
    </div>
  );

  const content = (
    <div className="c-date-time-picker__content">
      {step === Step.Date ? dateWheels : timeWheels}
      <CFlex justifyContent={JustifyContent.Center} class="c-date-time-picker__quick">
        <CButton
          text={step === Step.Date ? (props.translations?.today ?? translations.datePicker.today) : (props.translations?.now ?? translations.timePicker.now)}
          pattern={CButtonPattern.Secondary}
          shape={CButtonShape.Square}
          onClick={step === Step.Date ? handleToday : handleNow}
        />
      </CFlex>
    </div>
  );

  const actions = (
    <div className="c-date-time-picker__actions">
      {step === Step.Date ? (
        <>
          <CButton text={props.translations?.cancel ?? translations.dateTimePicker.cancel} pattern={CButtonPattern.Secondary} shape={CButtonShape.Square} onClick={handleCancel} />
          <CButton text={props.translations?.next ?? translations.dateTimePicker.next} pattern={CButtonPattern.Primary} shape={CButtonShape.Square} onClick={handleNext} />
        </>
      ) : (
        <>
          <CButton text={props.translations?.back ?? translations.dateTimePicker.back} pattern={CButtonPattern.Secondary} shape={CButtonShape.Square} onClick={handleBack} />
          <CButton text={props.translations?.confirm ?? translations.dateTimePicker.confirm} pattern={CButtonPattern.Primary} shape={CButtonShape.Square} onClick={handleConfirm} />
        </>
      )}
    </div>
  );

  return (
    <div
      id={props.id}
      ref={triggerRef}
      className={classNames('c-date-time-picker', {
        'c-date-time-picker--disabled': props.disabled,
        'c-date-time-picker--full-width': props.fullWidth,
        [`c-date-time-picker--status-${status}`]: status !== 'neutral',
      }, props.class)}
      data-test-selector={props.testSelector ?? 'c-date-time-picker'}
      style={props.style}
    >
      {props.label && (
        <CFieldLabel label={props.label} required={props.required} security={props.security} />
      )}

      <button
        type="button"
        className={classNames('c-date-time-picker__trigger', { 'c-date-time-picker__trigger--open': open })}
        onClick={handleOpen}
        disabled={props.disabled}
      >
        <CText size={CTextSize.Md} color={displayValue ? undefined : CTextColor.Gray5}>
          {displayValue || props.placeholder || ''}
        </CText>
      </button>

      {props.validations && props.validations.length > 0 && !props.hideMessageContainer && (
        <CFieldValidation validations={props.validations} />
      )}

      {open && createPortal(
        <>
          <div className="c-date-time-picker__overlay" onClick={handleCancel} />
          <div className="c-date-time-picker__dropdown" style={dropdownStyle}>
            {content}
            {actions}
          </div>
        </>,
        portalContainer ?? document.body
      )}

      {createPortal(
        <CBottomPanel
          scrollable={false}
          presenter={presenter}
          header={{ title: props.translations?.title ?? translations.dateTimePicker.title ?? props.label ?? '' }}
          footer={{
            slot: (
              <CFlex gap={CSpacing.Md} class="c-date-time-picker__footer-buttons">
                {step === Step.Date ? (
                  <>
                    <CButton icon={CIcons.X} text={props.translations?.cancel ?? translations.dateTimePicker.cancel} shape={CButtonShape.Square} pattern={CButtonPattern.Secondary} onClick={handleCancel} class="c-date-time-picker__footer-btn" />
                    <CButton icon={CIcons.ArrowRight} text={props.translations?.next ?? translations.dateTimePicker.next} shape={CButtonShape.Square} pattern={CButtonPattern.Primary} onClick={handleNext} class="c-date-time-picker__footer-btn" />
                  </>
                ) : (
                  <>
                    <CButton icon={CIcons.ArrowLeft} text={props.translations?.back ?? translations.dateTimePicker.back} shape={CButtonShape.Square} pattern={CButtonPattern.Secondary} onClick={handleBack} class="c-date-time-picker__footer-btn" />
                    <CButton icon={CIcons.Check} text={props.translations?.confirm ?? translations.dateTimePicker.confirm} shape={CButtonShape.Square} pattern={CButtonPattern.Primary} onClick={handleConfirm} class="c-date-time-picker__footer-btn" />
                  </>
                )}
              </CFlex>
            ),
          }}
        >
          {content}
        </CBottomPanel>,
        portalContainer ?? document.body
      )}
    </div>
  );
}
